import { useState, useRef, useCallback } from 'react';
import ProductCard from '@/components/feature/ProductCard';
import { products as mockProducts } from '@/mocks/products';
import { apiProductToProduct } from '@/services/adapters';
import type { ApiProduct } from '@/types/api';
import type { Product } from '@/types';

interface Props {
  featured?: ApiProduct[];
  latest?: ApiProduct[];
}

export default function ProductsSection({ featured, latest }: Props) {
  const [activeTab, setActiveTab] = useState('bestsellers');
  const scrollRef = useRef<HTMLDivElement>(null);

  const mappedFeatured: Product[] = (featured || []).map(p => apiProductToProduct(p));
  const mappedLatest: Product[] = (latest || []).map(p => apiProductToProduct(p));
  const mappedAll = [...mappedFeatured, ...mappedLatest];

  const tabs = [
    { id: 'bestsellers', label: 'الأكثر مبيعًا', products: mappedFeatured },
    { id: 'new', label: 'وصل حديثًا', products: mappedLatest },
    { id: 'all', label: 'كل المنتجات', products: mappedAll },
  ];

  const currentTab = tabs.find(t => t.id === activeTab) ?? tabs[0];

  const scroll = useCallback((direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const amount = direction === 'left' ? -300 : 300;
      scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
    }
  }, []);

  if (!featured && !latest) {
    return (
      <section className="section-padding bg-cream-50">
        <div className="container-main">
          <h2 className="heading-lg text-center mb-10">منتجات مختارة</h2>
          <div ref={scrollRef} className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4">
            {mockProducts.slice(0, 8).map(product => (
              <div key={product.id} className="w-[44%] md:w-[23%] flex-shrink-0 snap-start">
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="section-padding bg-cream-50">
      <div className="container-main">
        <h2 className="heading-lg text-center mb-6">منتجات مختارة</h2>
        <div className="flex items-center justify-center gap-2 mb-8 flex-wrap">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-charcoal-900 text-white'
                  : 'bg-cream-200 text-charcoal-600 hover:bg-cream-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="relative">
          {/* Desktop arrows */}
          <button onClick={() => scroll('right')} className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-cream-300 items-center justify-center text-charcoal-600 hover:bg-charcoal-900 hover:text-white transition-all -mr-4">
            <i className="ri-arrow-right-s-line text-lg" />
          </button>
          <button onClick={() => scroll('left')} className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-cream-300 items-center justify-center text-charcoal-600 hover:bg-charcoal-900 hover:text-white transition-all -ml-4">
            <i className="ri-arrow-left-s-line text-lg" />
          </button>
          <div ref={scrollRef} className="flex gap-3 md:gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 hide-scrollbar">
            {currentTab.products.slice(0, 12).map((product) => (
              <div key={product.id} className="w-[44%] md:w-[25%] lg:w-[20%] flex-shrink-0 snap-start">
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
